import { Deserializer } from 'jsonapi-serializer';
import { APIConnector } from './api/APIConnector';
import { genericError } from '../models/GenericError';
import { CREATED, INTERNAL_SERVER_ERROR, OK } from '../constants/response-status-codes';
import { processServerError, getConstraintViolation } from './utilities/ServiceError';
import { isSuccessStatus } from "../utilities/requestResponseValidator";
import { MasterGroupProfile } from '../models/MasterGroupProfile';

const apiConnector = new APIConnector();

/**
 * Find a specific (should only ever be 1 or none) MasterGroup with masterId and accessGroupId matching.
 * We need this in order to delete the link (accessGroup to master).
 * @param masterId
 * @param accessProfileId
 */
export const getMasterGroupProfile = async (masterId, accessProfileId): Promise<MasterGroupProfile[]> => {
    try {
        const url = 'masterGroupProfile?filter=master.id==' + masterId + ';accessProfile.id==' + accessProfileId + '&include=accessProfile,master';
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, results: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }
                const masterGroups: MasterGroupProfile[] = results.map((m): MasterGroupProfile => {
                    return {
                        id: m['id'],
                        accessProfileId: m['access-profile']['id'],
                        masterId: m['master']['id']
                    };
                });

                return resolve(masterGroups);
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        })
    }
}

export const getMasterIdsForAccessProfile = async (accessProfileId: string): Promise<string[]> => {
    try {
        const url = 'masterGroupProfile?include=master&filter=accessProfile.id==' + accessProfileId;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(json, (err: any, masterGroupProfiles: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }
                const masterIds: string[] = masterGroupProfiles.map((m) => {
                    return m['master']['id']
                });

                return resolve(masterIds);
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

/**
 * Send a request to Rest Servlet to assign 1 or more AccessProfile's to this Master.
 * The backend will handle assigning MasterGroup's to the Master based on what's assigned to the AccessProfile.
 * @param masterId
 * @param accessGroup
 */
export const storeMasterGroupProfiles = async (newRecord) => {
    try {

        const url = '';
        const response = await apiConnector.sendData(url, true, newRecord, true)

        return new Promise(async (resolve, reject) => {
            if (response) {
                if (response.status === OK || response.status === CREATED) {
                    try {
                        const json = await response.json()
                        return resolve({ id: json['attributes']?.['id'], success: true })
                    }
                    catch (exception) {
                        return reject({ status: response.status, id: newRecord.relationships.masterId, message: genericError, success: false });
                    }
                }
                else {
                    if (response.body) {
                        if (response.statusText) {
                            const content = await response.json();
                            return reject({ status: response.status, id: newRecord.relationships.masterId, success: false, message: content.errors.map(error => getConstraintViolation(error.detail)) });
                        }
                        else {
                            return reject({ status: response.status, id: newRecord.relationships.masterId, message: genericError, success: false });
                        }
                    }
                    else {
                        return reject({ status: response.status, id: newRecord.relationships.masterId, message: genericError, success: false });
                    }
                }
            }
            else {
                return reject({ status: INTERNAL_SERVER_ERROR, id: newRecord.relationships.masterId, message: genericError, success: false });
            }
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

/**
 * Delete the MasterGroup for this person and accessGroup.
 * Effectively removes the access group assignment from the person.
 * @param masterId
 * @param accessGroup
 */
export const deleteMasterGroupProfile = async (masterId: string, accessProfileId): Promise<boolean> => {
    try {
        // First lookup the MasterGroup
        const masterGroupProfiles = await getMasterGroupProfile(masterId, accessProfileId);

        // Found the MasterGroup, let's delete it then.
        if (masterGroupProfiles && masterGroupProfiles.length > 0) {
            const masterGroupId = masterGroupProfiles[0].id;
            const url = `masterGroupProfile/` + masterGroupId;
            let response: Response;

            const toBeDeleted = {
                data: {
                    type: "masterGroupProfile",
                    id: masterGroupId
                }
            };

            await apiConnector.deleteData(url, toBeDeleted).then(async (data) => {
                response = data;
            }).catch(error => {
                return new Promise((resolve, reject) => {
                    reject({ status: response.status, message: genericError })
                })
            });

            return new Promise(async (resolve, reject) => {
                if (response && isSuccessStatus(response.status)) {
                    return resolve(true);
                }
                const json = await response.json();

                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            });
        }
        // Unable to delete the MasterGroup. Couldn't find it.
        else {
            return new Promise<boolean>(async (resolve, reject) => {
                reject({ status: INTERNAL_SERVER_ERROR, message: genericError });
            });
        }
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

/**
 * 
 * @param toBeAssigned 
 * @param toBeUnassigned 
 * @param masterId 
 * @returns 
 */
export const assignAndUnassignMasterGroupProfile = async (toBeAssigned: string[], toBeUnassigned: string[], itemId: string, type: "MASTER" | "ACCESS_PROFILE") => {
    const newMasterGroupProfiles = buildPayloadForAssign(toBeAssigned, itemId, type);
    const addedMasterGroupProfiles: Promise<boolean>[] = [];
    let removedMasterGroupProfiles: Promise<boolean>[] = [];

    // First unassign
    if (toBeUnassigned.length > 0) {
        removedMasterGroupProfiles = toBeUnassigned.map(async (id) => {
            if (type === "ACCESS_PROFILE") {
                return await deleteMasterGroupProfile(id, itemId,);
            }

            return await deleteMasterGroupProfile(itemId, id);
        });

        await Promise.all(removedMasterGroupProfiles);
    }

    // Only send request to assign if we have something selected
    if (toBeAssigned.length > 0) {
        await storeMasterGroupProfiles(newMasterGroupProfiles);
    }

    return Promise.all([...addedMasterGroupProfiles, ...removedMasterGroupProfiles]);
}

const buildPayloadForAssign = (toBeAssigned: string[], itemId: string, type: string) => {
    let masterGroupProfile = {
        type: "masterGroupProfiles",
        relationships: {
            masterId: 0,
            accessProfileId: 0,
            toBeAssigned: toBeAssigned
        }
    }

    if (type === "ACCESS_PROFILE") {
        masterGroupProfile.relationships.accessProfileId = parseInt(itemId);
    }
    else if (type === "MASTER") {
        masterGroupProfile.relationships.masterId = parseInt(itemId);
    }

    return masterGroupProfile;
}
